import { Bot, Workflow, Users, GitBranch, Network, Brain } from 'lucide-react';
import type { AgentData } from '../lib/api';

const MODE_ICONS: Record<string, typeof Bot> = {
  standard: Bot, workflow: Workflow, crew: Users, pipeline: GitBranch,
};
const MODE_COLORS: Record<string, string> = {
  standard: 'bg-blue-500/10 text-blue-400',
  workflow: 'bg-amber-500/10 text-amber-400',
  crew: 'bg-emerald-500/10 text-emerald-400',
  pipeline: 'bg-purple-500/10 text-purple-400',
};

interface Props {
  agent: AgentData;
  onClick: () => void;
}

export function AgentCard({ agent, onClick }: Props) {
  const ModeIcon = MODE_ICONS[agent.executionMode] ?? Bot;
  const modeColor = MODE_COLORS[agent.executionMode] ?? 'bg-zinc-500/10 text-zinc-400';

  return (
    <button onClick={onClick} className="w-full text-left bg-surface border border-border rounded-lg p-4 hover:border-accent/50 transition-all group">
      {/* Header */}
      <div className="flex items-start justify-between mb-1.5">
        <h3 className="font-semibold text-sm text-zinc-100 group-hover:text-accent-2 transition-colors">{agent.name}</h3>
        <code className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-accent-2 font-mono shrink-0">{agent.trigger}</code>
      </div>

      {agent.description && (
        <p className="text-xs text-zinc-500 mb-2.5 line-clamp-2">{agent.description}</p>
      )}

      {/* Badges row */}
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${modeColor}`}>
          <ModeIcon className="w-3 h-3" />
          {agent.executionMode}
        </span>
        {agent.thinking && agent.thinking !== 'off' && (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400">
            <Brain className="w-3 h-3" />
            {agent.thinking}
          </span>
        )}
        {agent.mesh && (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400">
            <Network className="w-3 h-3" />
            mesh
          </span>
        )}
        {agent.isFactoryAgent && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400">factory</span>
        )}
        {agent.runtimeId && agent.runtimeId !== 'default' && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500">rt:{agent.runtimeId}</span>
        )}
      </div>

      {/* Model */}
      {agent.model && (
        <div className="text-[10px] text-zinc-600 font-mono mb-1.5 truncate">{agent.model}</div>
      )}

      {/* Tools */}
      {agent.tools && agent.tools.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {agent.tools.slice(0, 8).map(t => (
            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-surface-2 text-zinc-500 font-mono">{t}</span>
          ))}
          {agent.tools.length > 8 && (
            <span className="text-[10px] text-zinc-600">+{agent.tools.length - 8}</span>
          )}
        </div>
      )}

      {/* Shared scopes / crew info */}
      {(agent.sharedScopes?.length || agent.crewPlannerAgent) && (
        <div className="mt-1.5 text-[10px] text-zinc-600">
          {agent.sharedScopes?.length ? `scopes: ${agent.sharedScopes.join(', ')}` : ''}
          {agent.crewPlannerAgent ? ` · crew: ${agent.crewPlannerAgent}/${agent.crewWorkerAgent}` : ''}
        </div>
      )}
    </button>
  );
}
